import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Logo } from "./Logo";
import { navigation, contactInfo } from "../data/mock";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg"
          : "bg-black/20 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 group"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <Logo size="default" />
            <div className="flex flex-col">
              <span
                className={`font-bold text-lg leading-tight transition-colors ${
                  isScrolled ? "text-primary" : "text-white"
                }`}
              >
                Gerüst Sanierung
              </span>
              <span
                className={`text-xs transition-colors ${
                  isScrolled ? "text-secondary" : "text-white/70"
                }`}
              >
                Robert Recher
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isScrolled ? "text-neutral-700" : "text-white/90"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${contactInfo.phoneShort.replace(/\s/g, "")}`}
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                isScrolled ? "text-neutral-600" : "text-white/90"
              }`}
            >
              <Phone className="w-4 h-4" />
              <span>{contactInfo.phone}</span>
            </a>
            <Button
              onClick={() => scrollToSection("#kontakt")}
              className="bg-accent hover:bg-accent/90 text-neutral-900 font-semibold px-6"
            >
              Anfragen
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? "text-neutral-800" : "text-white"}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? "text-neutral-800" : "text-white"}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-4 py-6 space-y-4">
          {navigation.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollToSection(item.href)}
              className="block w-full text-left text-neutral-700 font-medium py-2 hover:text-primary transition-colors"
            >
              {item.label}
            </button>
          ))}
          <div className="pt-4 border-t border-neutral-200">
            <a
              href={`tel:${contactInfo.phoneShort.replace(/\s/g, "")}`}
              className="flex items-center gap-2 text-primary font-medium py-2"
            >
              <Phone className="w-4 h-4" />
              {contactInfo.phone}
            </a>
          </div>
          <Button
            onClick={() => scrollToSection("#kontakt")}
            className="w-full bg-accent hover:bg-accent/90 text-neutral-900 font-semibold mt-4"
          >
            Anfragen
          </Button>
        </nav>
      </div>
    </header>
  );
};
